<script setup>
// Handicap calculator: pick any two entrants of THIS competition and see the match they would
// play — each side's handicap, the netted start, the six-game pattern and, for tennis / padel,
// the tie-break starts (7-point and match tie-break where the scoring system plays them).
// Stateless: the server resolves it from the competition's saved settings and the two values.
//   POST api/v2/handicapschemes/preview
import {computed, ref, watch} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import HandicapMatchBanner from '~/components/handicaps/HandicapMatchBanner.vue';
import {useHandicapApi} from '~/composables/useHandicapApi';
import {useHandicaps} from '~/composables/useHandicaps';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  settings: {type: Object, default: null},
  // [{teamId, playerId?, name, value}] — the competition's entrants, in the page's order
  entries: {type: Array, default: () => []},
  // the competition's scoring system — decides which tie-breaks exist
  scoringSystemId: {type: String, default: null},
  noun: {type: String, default: 'player'},
  // americano: a side is a PAIR of players. Each side gets two pickers and the server combines the
  // two values exactly as it does for a real match (nearest option to the mean; bowls sums).
  pairs: {type: Boolean, default: false},
});
const emit = defineEmits(['update:modelValue']);

const {preview} = useHandicapApi();
const {labelFor, chipClassFor} = useHandicaps();

// one key per picker: singles use [0] on each side, pairs use both
const homeKeys = ref([null, null]);
const awayKeys = ref([null, null]);
const result = ref(null);
const loading = ref(false);
const error = ref(null);

const keyOf = (e) => `${e.teamId}:${e.playerId ?? ''}`;
const items = computed(() => props.entries.map((e) => ({
  key: keyOf(e), name: e.name, value: e.value == null ? null : Number(e.value),
  title: `${e.name} · ${labelFor(props.settings, e.value) ?? 'Not set'}`,
})));
const byKey = (k) => items.value.find((i) => i.key === k) ?? null;
const slots = computed(() => props.pairs ? 2 : 1);
// Every picker offers everyone. Picking a player who already sits in another slot SWAPS the two,
// so a four-player division stays rearrangeable (excluding taken players left nothing to choose).
const setSlot = (side, slot, key) => {
  const mine = side === 'home' ? homeKeys : awayKeys;
  const previous = mine.value[slot] ?? null;
  for (const keys of [homeKeys, awayKeys]) {
    for (let i = 0; i < keys.value.length; i++) {
      if (keys.value[i] === key && !(keys === mine && i === slot)) keys.value[i] = previous;
    }
  }
  mine.value[slot] = key;
};
const pick = (keys) => keys.slice(0, slots.value).map(byKey).filter(Boolean);
const home = computed(() => pick(homeKeys.value));
const away = computed(() => pick(awayKeys.value));
const sideName = (side) => side.map((p) => p.name).join(' & ');
const homeName = computed(() => sideName(home.value));
const awayName = computed(() => sideName(away.value));
const sideReady = (side) => side.length === slots.value;
const needed = computed(() => slots.value * 2);

watch(() => props.modelValue, (open) => {
  if (!open) return;
  const keys = items.value.map((i) => i.key);
  homeKeys.value = props.pairs ? [keys[0] ?? null, keys[1] ?? null] : [keys[0] ?? null, null];
  awayKeys.value = props.pairs ? [keys[2] ?? null, keys[3] ?? null] : [keys[1] ?? null, null];
  result.value = null;
  error.value = null;
  run();
});

const run = async () => {
  if (!sideReady(home.value) || !sideReady(away.value) || !props.settings) { result.value = null; return; }
  loading.value = true;
  error.value = null;
  try {
    const s = props.settings;
    const res = await preview({
      schemeKey: s.schemeKey,
      customUnitSingular: s.customUnitSingular ?? null,
      customUnitPlural: s.customUnitPlural ?? null,
      directionOverride: s.directionOverride ?? null,
      autoAdjustMode: s.autoAdjustMode ?? 0,
      autoStepClose: s.autoStepClose, autoStepNormal: s.autoStepNormal, autoStepDecisive: s.autoStepDecisive,
      proportionalK: s.proportionalK, maxChangePerMatch: s.maxChangePerMatch,
      minValue: s.minValue, maxValue: s.maxValue,
      tiebreakHandicap: s.tiebreakHandicap ?? 1,
      // singles send the one value; pairs send both players' and the server combines them
      homeHandicap: home.value[0]?.value ?? 0,
      awayHandicap: away.value[0]?.value ?? 0,
      ...(props.pairs ? {
        homeHandicaps: home.value.map((p) => p.value), awayHandicaps: away.value.map((p) => p.value),
        homePlayerNames: home.value.map((p) => p.name), awayPlayerNames: away.value.map((p) => p.name),
      } : {}),
      homeName: homeName.value,
      awayName: awayName.value,
      scoringSystemId: props.scoringSystemId,
    });
    result.value = res?.start ?? null;
  } catch (e) {
    error.value = e?.global?.[0] ?? e?.message ?? 'Could not calculate the handicap.';
  } finally {
    loading.value = false;
  }
};
watch([homeKeys, awayKeys], run, {deep: true});

const swap = () => { const h = homeKeys.value; homeKeys.value = awayKeys.value; awayKeys.value = h; };
const close = () => emit('update:modelValue', false);
const sideLabel = (letter) => props.pairs ? `Pair ${letter}` : `${props.noun === 'team' ? 'Team' : 'Player'} ${letter}`;
</script>

<template>
  <tl-dialog :model-value="modelValue" title="Handicap calculator" :max-width="640"
             @update:model-value="emit('update:modelValue', $event)">
    <div class="hcd-intro">
      <template v-if="pairs">Pick the two players on each side to see the match those pairs would play. Each pair's handicap combines its players' the way a real match does.</template>
      <template v-else>Pick two {{ noun }}s to see the match they would play off their current handicaps.</template>
    </div>

    <div class="hcd-pick">
      <div class="hcd-side">
        <div class="tl-label mb-1">{{ sideLabel('A') }}</div>
        <v-autocomplete v-for="slot in slots" :key="`h${slot}`" :model-value="homeKeys[slot - 1]" :items="items"
                        item-value="key" item-title="title" density="compact" variant="outlined" hide-details
                        :placeholder="pairs ? `Player ${slot}` : `Choose a ${noun}`" :class="{'mt-2': slot > 1}"
                        @update:model-value="setSlot('home', slot - 1, $event)">
          <template #item="{ props: ip, item }">
            <v-list-item v-bind="ip" :title="item.raw.name">
              <template #append>
                <span class="tl-chip" :class="chipClassFor(settings, item.raw.value)">{{ labelFor(settings, item.raw.value) ?? 'Not set' }}</span>
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>
      <v-btn icon="mdi-swap-horizontal" size="small" variant="text" class="hcd-swap" @click="swap"/>
      <div class="hcd-side">
        <div class="tl-label mb-1">{{ sideLabel('B') }}</div>
        <v-autocomplete v-for="slot in slots" :key="`a${slot}`" :model-value="awayKeys[slot - 1]" :items="items"
                        item-value="key" item-title="title" density="compact" variant="outlined" hide-details
                        :placeholder="pairs ? `Player ${slot}` : `Choose a ${noun}`" :class="{'mt-2': slot > 1}"
                        @update:model-value="setSlot('away', slot - 1, $event)">
          <template #item="{ props: ip, item }">
            <v-list-item v-bind="ip" :title="item.raw.name">
              <template #append>
                <span class="tl-chip" :class="chipClassFor(settings, item.raw.value)">{{ labelFor(settings, item.raw.value) ?? 'Not set' }}</span>
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="my-3"/>
    <div v-else-if="result && result.isLevel" class="hcd-level tl-card">
      <v-icon size="18" icon="mdi-scale-balance" color="primary"/>
      Level: {{ homeName }} and {{ awayName }} play off scratch.
    </div>
    <handicap-match-banner v-else-if="result" :start="result" class="mt-3"/>
    <div v-else-if="items.length < needed" class="hcd-empty">{{ pairs ? 'Four players' : `Two ${noun}s` }} are needed before there is anything to calculate.</div>
    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mt-3">{{ error }}</v-alert>

    <template #actions>
      <v-btn variant="text" @click="close">Close</v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.hcd-intro { font-size: 13px; color: rgb(var(--v-theme-textSubtle)); margin-bottom: 12px; }
.hcd-pick { display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); gap: 8px; align-items: end; }
.hcd-side { min-width: 0; }
.hcd-swap { margin-bottom: 2px; }
.hcd-level { display: flex; align-items: center; gap: 8px; padding: 12px 14px; margin-top: 12px; font-size: 13px; font-weight: 600; }
.hcd-empty { padding: 14px; font-size: 13px; color: rgb(var(--v-theme-textSubtle)); text-align: center; }
@media (max-width: 520px) { .hcd-pick { grid-template-columns: 1fr; } .hcd-swap { display: none; } }
</style>
